import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useKeyboardStore } from "@/features/auth/stores/keyboardStore"
import { useBreakpoint } from "@/shared/hooks/useBreakpoint"
import { Keyboard } from "@capacitor/keyboard"
import { Capacitor } from "@capacitor/core"
import KeyboardChevronDown from "@/shared/icons/pikaicons/stroke/devices/keyboard-chevron-down.svg?react"
import { NativeBridge } from "@/shared/plugins/NativeBridge"

/** A real keyboard reports keyboardWillHide well within this after Keyboard.hide(). */
const KEYBOARD_HIDE_GRACE_MS = 400

/**
 * Floating top-right button for dismissing the on-screen keyboard.
 *
 * Shown when the on-screen keyboard is open on mobile.
 * Renders a keyboard-chevron-down icon. Tapping hides the keyboard.
 *
 * Selection mode "Done" is now handled by TopNavBar (iOS standard pattern:
 * Select All left, count center, Done right).
 *
 * Only rendered on mobile. Desktop uses Escape key and click-outside.
 */
export function FloatingDoneButton() {
  const { t } = useTranslation()
  const { isMobile } = useBreakpoint()
  const keyboardOpen = useKeyboardStore((s) => s.keyboard?.isOpen ?? false)

  // ── Animated mount / unmount ────────────────────────────────────────
  const showKeyboard = isMobile && keyboardOpen

  const [kbMounted, setKbMounted] = useState(false)
  const [kbVisible, setKbVisible] = useState(false)
  const kbTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  // Keyboard button lifecycle
  useEffect(() => {
    if (showKeyboard) {
      clearTimeout(kbTimerRef.current)
      setKbMounted(true)
      requestAnimationFrame(() => requestAnimationFrame(() => setKbVisible(true)))
    } else {
      setKbVisible(false)
      kbTimerRef.current = setTimeout(() => setKbMounted(false), 180)
    }
    return () => clearTimeout(kbTimerRef.current)
  }, [showKeyboard])

  if (!isMobile) return null

  const positionStyle = {
    // Anchor below the focus banner (if any) so the FAB floats over content
    // rather than overlapping the red "In Focus" banner. Its height is
    // published live via ResizeObserver in FocusBanner.tsx.
    top: "calc(var(--status-bar-height, 0px) + var(--focus-banner-height, 0px) + 10px)",
    right: 16,
  }

  const transitionStyle = {
    transition: "opacity 150ms ease-out, transform 150ms ease-out",
  }

  return (
    <>
      {/* Keyboard dismiss button */}
      {kbMounted && (
        <button
          type="button"
          data-floating-button
          className="fixed z-[60] flex items-center justify-center rounded-lg bg-primary p-2.5 text-primary-foreground shadow-lg"
          style={{
            ...positionStyle,
            ...transitionStyle,
            opacity: kbVisible ? 1 : 0,
            transform: kbVisible ? "scale(1)" : "scale(0.9)",
          }}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (Capacitor.isNativePlatform()) {
              void Keyboard.hide()
              // A keyboard that vanished while the app was in the background leaves this button up
              // (and on iOS the web view short): hiding finds nothing, so no hide event comes back.
              setTimeout(() => {
                if (!useKeyboardStore.getState().keyboard?.isOpen) return
                if (Capacitor.getPlatform() === "ios") void NativeBridge.repairKeyboard().catch(() => undefined)
                useKeyboardStore.getState().clearKeyboard()
              }, KEYBOARD_HIDE_GRACE_MS)
            }
          }}
          onPointerDown={(e) => e.stopPropagation()}
          aria-label={t("Dismiss keyboard")}
        >
          <KeyboardChevronDown className="size-6" />
        </button>
      )}
    </>
  )
}
